/**
 * AccessMethodsList - Generic component for displaying access methods
 *
 * Shows up to 3 items (subscription label + bundles) with "+n more" tooltip
 * for remaining items.
 */

import { ReactNode } from 'react';
import { Col, Row } from '@design-system/components/grid/grid';
import { Typography } from '@design-system/components/typography/typography';
import { Tooltip } from '@design-system/components/tooltip/tooltip';
import { AccessMethodItem } from '../viewModel/extras.viewModel.types';

type AccessMethodsListProps = {
  items: AccessMethodItem[];
  maxVisible?: number;
  renderItem: (item: AccessMethodItem, index: number) => ReactNode;
  direction?: 'row' | 'column';
  itemsGap?: 's' | 'm' | 'l';
};

export const AccessMethodsList = (p: AccessMethodsListProps) => {
  const maxVisible = p.maxVisible ?? 2;
  const direction = p.direction ?? 'column';

  const shouldShowAll = p.items.length <= maxVisible + 1;
  const visibleItems = shouldShowAll ? p.items : p.items.slice(0, maxVisible);
  const remainingItems = shouldShowAll ? [] : p.items.slice(maxVisible);
  const hasMore = remainingItems.length > 0;

  const remainingItemsTooltip = remainingItems
    .map((item) => (item.type === 'bundle' ? item.title : item.text))
    .join('\n');

  const Container = direction === 'row' ? Row : Col;

  return (
    <Container gap={p.itemsGap ?? 'xs'} alignCenter={direction === 'row'}>
      {visibleItems.map((item, index) => p.renderItem(item, index))}
      {hasMore && (
        <Tooltip
          arrow
          variant="dark"
          title={
            <div style={{ whiteSpace: 'pre-line', padding: '4px' }}>
              {remainingItemsTooltip}
            </div>
          }
        >
          <Typography variant="body" size="small" style={{ cursor: 'help' }}>
            +{remainingItems.length} more
          </Typography>
        </Tooltip>
      )}
    </Container>
  );
};
